import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '../common/LoadingSpinner';

const LOAD_TIMEOUT_MS = 60_000;

interface PreviewIframeProps {
  previewUrl?: string;
  /** Whether the project has any generated code yet, independent of sandbox status. */
  hasFiles: boolean;
  /** Whether a build is currently queued or in progress. */
  isBuilding: boolean;
  /**
   * Incremented by the parent on every transition into `running`. Remounts
   * the iframe with a cache-busted URL so a 404 caught during the rebuild's
   * brief nginx-route window can never stick until a manual refresh.
   */
  reloadEpoch: number;
}

function MonitorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1}
        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

// ─── Empty-content states (no live preview URL yet) ────────────────────────
// Three distinct situations, so the large preview area always says
// something true rather than a generic placeholder regardless of context.

function BuildingState({ t }: { t: (key: string) => string }) {
  return (
    <div className="flex-1 flex items-center justify-center bg-white">
      <div className="text-center px-6">
        <span
          className="mx-auto mb-4 block h-3 w-3 rounded-full bg-amber-400 animate-pulse"
          aria-hidden="true"
        />
        <p className="text-base font-medium text-gray-700">{t('preview.buildingHint')}</p>
        <p className="mt-1 text-sm text-gray-500">{t('preview.buildingHintSubtitle')}</p>
      </div>
    </div>
  );
}

function FirstRunGuidance({ t }: { t: (key: string) => string }) {
  const steps = [t('preview.emptyState.step1'), t('preview.emptyState.step2'), t('preview.emptyState.step3')];
  return (
    <div className="flex-1 overflow-y-auto bg-neutral-50 flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-dashed border-neutral-300 bg-white p-8 text-center shadow-sm">
        <MonitorIcon className="mx-auto h-10 w-10 text-neutral-300" />
        <p className="mt-4 mb-1 text-sm font-semibold uppercase tracking-[0.16em] text-neutral-500">
          {t('preview.emptyState.eyebrow')}
        </p>
        <p className="text-lg font-semibold text-neutral-900">{t('preview.noPreview')}</p>
        <p className="mt-1 text-sm text-neutral-600">{t('preview.noPreviewSubtitle')}</p>

        <ol className="mt-6 space-y-3 text-left">
          {steps.map((step, i) => (
            <li
              key={i}
              className="flex items-start gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
                {i + 1}
              </span>
              <span className="text-sm text-neutral-700">{step}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function NotRunningState({ t }: { t: (key: string) => string }) {
  return (
    <div className="flex-1 flex items-center justify-center bg-gray-100">
      <div className="text-center text-gray-500 px-6">
        <MonitorIcon className="mx-auto h-12 w-12 mb-4 text-gray-300" />
        <p>{t('preview.notRunning')}</p>
        <p className="text-sm mt-1">{t('preview.notRunningSubtitle')}</p>
      </div>
    </div>
  );
}

export function PreviewIframe({ previewUrl, hasFiles, isBuilding, reloadEpoch }: PreviewIframeProps) {
  const { t } = useTranslation('project');
  const [isLoading, setIsLoading] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Cache-busted URL: the epoch changes on every running-transition, so the
  // browser can never serve a cached 404 from the rebuild window.
  const bustedUrl = previewUrl
    ? `${previewUrl}${previewUrl.includes('?') ? '&' : '?'}_r=${reloadEpoch}`
    : undefined;

  useEffect(() => {
    if (!previewUrl) return;
    setIsLoading(true);
    setTimedOut(false);
    setLoadError(null);
    const timer = setTimeout(() => setTimedOut(true), LOAD_TIMEOUT_MS);

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string };
      if (data?.type === 'preview_loaded') {
        setIsLoading(false);
        setTimedOut(false);
        clearTimeout(timer);
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('message', onMessage);
    };
  }, [previewUrl, reloadKey, reloadEpoch]);

  if (!previewUrl) {
    if (isBuilding) return <BuildingState t={t} />;
    if (!hasFiles) return <FirstRunGuidance t={t} />;
    return <NotRunningState t={t} />;
  }

  function handleLoad() {
    setIsLoading(false);
    setTimedOut(false);
    setLoadError(null);
  }

  function handleReload() {
    setReloadKey((k) => k + 1);
  }

  function handleError() {
    setIsLoading(false);
    setTimedOut(false);
    setLoadError('Preview failed to load. Please try reloading.');
  }

  return (
    <div className="flex-1 relative bg-white">
      {isLoading && !timedOut && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
          <LoadingSpinner size="lg" />
        </div>
      )}
      {timedOut && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm px-4 py-2 rounded-lg shadow-md flex items-center gap-3 whitespace-nowrap">
          <span>Preview is still loading. Verification and repair steps can delay first paint.</span>
          <button
            onClick={handleReload}
            className="font-medium underline hover:no-underline"
          >
            Reload
          </button>
        </div>
      )}
      {loadError && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-2 rounded-lg shadow-md flex items-center gap-3 whitespace-nowrap">
          <span>{loadError}</span>
          <button
            onClick={handleReload}
            className="font-medium underline hover:no-underline"
          >
            Reload
          </button>
        </div>
      )}
      <iframe
        key={`${reloadEpoch}-${reloadKey}`}
        src={bustedUrl}
        className="w-full h-full border-0"
        onLoad={handleLoad}
        onError={handleError}
        title="App Preview"
      />
    </div>
  );
}
